/**
 * "Pull from traces": a live dataset on a trace source. Name it, pick the
 * trace connection (Langfuse or qFlow), filter what it holds (Langfuse's own
 * filter box, or qFlow's run filters) and cap how many one run keeps. Each experiment run then pulls its period into a
 * new version. Nothing is pulled here: the dataset is created empty and stays
 * so until an experiment runs on it. Used by the new-experiment flow and the
 * Datasets page.
 */

import { useState } from "react";
import { Button, ButtonLink } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Dialog } from "@/components/ui/overlay";
import { Select } from "@/components/ui/select";
import { errorText } from "@/components/ui/states";
import type { Dataset } from "../types";
import { useTraceSources } from "@/features/traces/hooks";
import { QFLOW } from "@/features/traces/sources";
import { SourceFilters } from "./source-filters";
import { NEW_QUERY, toFilters, type TraceQuery } from "@/features/traces/trace-query";
import { useApiMutation } from "@/lib/query";
import type { components } from "@/lib/api-schema";

type LiveDatasetIn = components["schemas"]["LiveDatasetIn"];

export function LiveDatasetDialog({
  initialName = "",
  onCreated,
  onClose,
}: {
  initialName?: string;
  onCreated: (dataset: Dataset) => void;
  onClose: () => void;
}) {
  const { sources, isPending } = useTraceSources();
  const [name, setName] = useState(initialName);
  const [picked, setPicked] = useState<string | null>(null);
  const sourceId = picked ?? sources[0]?.id ?? "";
  const [query, setQuery] = useState<TraceQuery>(NEW_QUERY);
  const [limit, setLimit] = useState(100);
  const create = useApiMutation<Dataset, LiveDatasetIn>("post", "/datasets/live", {
    invalidate: ["/datasets"],
  });
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const ready = !!name.trim() && !!sourceId && !blocked && !create.isPending;

  const submit = () =>
    create.mutate(
      { name: name.trim(), connection_id: sourceId, filters: toFilters(query), limit },
      { onSuccess: onCreated, onError: (e) => setError(errorText(e, "Could not create the dataset.")) },
    );

  return (
    <Dialog
      title="Pull from traces"
      subtitle="A live dataset: each experiment run pulls the matching traces or runs of its period into a new version."
      onClose={onClose}
      width={720}
      footer={
        <>
          {error && (
            <span role="alert" className="text-xs text-err">
              {error}
            </span>
          )}
          {blocked && !error && <span className="text-xs text-muted">{blocked}</span>}
          <span className="flex-1" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready} onClick={submit}>
            Create dataset
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" htmlFor="live-name">
          <input
            id="live-name"
            className="inp"
            value={name}
            placeholder="e.g. Case inclusion · production traces"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        {!isPending && sources.length === 0 ? (
          <p className="text-[13px] text-muted">
            No trace source yet.{" "}
            <ButtonLink size="sm" to="/settings?tab=connections&add=1">
              Add a Langfuse or qFlow connection
            </ButtonLink>
          </p>
        ) : (
          <>
            <Field label="Trace source" htmlFor="live-source">
              <Select
                id="live-source"
                value={sourceId}
                // Filters do not carry over: each platform has its own columns.
                onChange={(id) => (setPicked(id), setQuery(NEW_QUERY))}
                options={sources.map((s) => ({
                  value: s.id,
                  label: s.name,
                  hint: s.connector_type === QFLOW ? "qFlow" : "Langfuse",
                }))}
              />
            </Field>
            <SourceFilters sourceId={sourceId} value={query} onChange={setQuery} onBlocked={setBlocked} />
            <Field
              label="Keep per run"
              htmlFor="live-limit"
              hint="At most this many: the first ones from the period start."
            >
              <input
                id="live-limit"
                type="number"
                min={1}
                max={5000}
                className="inp w-32 font-mono"
                value={limit}
                onChange={(e) => setLimit(Math.max(1, Math.min(5000, Number(e.target.value) || 1)))}
              />
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}
